/**
 * @module types/forms
 * @description Form-related type definitions for checkout form state management
 */

import { ActionMap } from './utils';

/**
 * @enum FormFieldName
 * @description Enum for all checkout form field names
 */
export enum FormFieldName {
  NAME = 'name',
  COMPANY = 'company',
  COUNTRY = 'country',
  COMPANY_ID = 'companyId',
  PROVINCE = 'province',
  ZIP = 'zip',
  WEBSITE = 'website',
  STREET2 = 'street2',
  REFERENCE = 'reference',
  NOTE = 'note',
  EMAIL = 'email',
  STREET = 'street',
  CITY = 'city',
  PHONE = 'phone',
}

/**
 * @enum FormActionType
 * @description Enum for form reducer action types
 */
export enum FormActionType {
  INIT = 'init',
  RESET = 'reset',
  SET_VALUE = 'setValue',
  SET_ERROR = 'setError',
  UNSET_ERROR = 'unsetError',
  VALIDATE = 'validate',
}

/**
 * @enum ValidationRuleType
 * @description Enum for validation rule types used in error summaries
 */
export enum ValidationRuleType {
  REQUIRED = 'required',
  INVALID = 'invalid',
}

/**
 * @interface FormValues
 * @description Map of form field values
 */
export interface FormValues {
  [key: string]: string | number | undefined;
}

/**
 * @interface FormErrors
 * @description Map of form field errors (string message or false if no error)
 */
export interface FormErrors {
  [key: string]: string | false;
}

/**
 * @interface FormState
 * @description Complete form state including values, errors, and error summary
 */
export interface FormState {
  values: FormValues;
  errors: FormErrors;
  errorSummary?: string[] | null;
}

/**
 * @interface FormActionPayloadMap
 * @description Maps each FormActionType to its corresponding payload type
 */
export interface FormActionPayloadMap {
  [FormActionType.INIT]: Partial<FormState>;
  [FormActionType.RESET]: Partial<FormState> | undefined;
  [FormActionType.SET_VALUE]: Record<string, string>;
  [FormActionType.SET_ERROR]: Record<string, string>;
  [FormActionType.UNSET_ERROR]: string;
  [FormActionType.VALIDATE]: string;
}

/**
 * @type FormAction
 * @description Discriminated union of all form actions with full type safety.
 * Generated from FormActionPayloadMap using ActionMap helper.
 */
export type FormAction = ActionMap<FormActionPayloadMap>;

/**
 * @type SetRequiredFieldFn
 * @description Function type for setting a field as required
 */
export type SetRequiredFieldFn = (fieldName: string) => void;

/**
 * @interface CheckoutFormProps
 * @description Props injected by withCheckoutFormContext HOC
 */
export interface CheckoutFormProps {
  form: FormState;
  validate: () => boolean;
  setValue: (field: string, val: string | number) => void;
  init: (obj: Partial<FormState>) => void;
  reset: (obj?: Partial<FormState>) => void;
}

/**
 * @interface UseFieldReturn
 * @description Return type for useField hook
 */
export interface UseFieldReturn {
  value: string | number | undefined;
  error: string | false | undefined;
  setError: (msg: string) => void | false;
  setValue: (val: string | number) => void;
  unsetError: () => void;
  validate: () => void;
}
